import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/lib/api-client";
import {
  blockAdminCustomer,
  getAdminCustomerDetail,
  unblockAdminCustomer,
  type AdminCustomerStatus,
} from "@/lib/admin-api-client";
import { describeAdminError } from "@/components/admin/adminErrors";
import { formatDateFR } from "@/components/admin/adminFormat";

const STATUS_LABELS: Record<AdminCustomerStatus, string> = {
  ACTIVE: "Actif",
  BLOCKED: "Bloqué",
  GUEST: "Invité",
};

const STATUS_BADGE_CLASSES: Record<AdminCustomerStatus, string> = {
  ACTIVE: "bg-green-100 text-green-800",
  BLOCKED: "bg-red-100 text-red-800",
  GUEST: "bg-cream2 text-muted",
};

/**
 * `/admin/clients/:id` — équivalent de apps/web/app/(admin)/admin/clients/[id]/page.tsx :
 * fiche client (coordonnées, statut, valeur vie client, historique de
 * commandes) + action bloquer/débloquer. Pas de lien vers le détail d'une
 * commande dans l'original apps/web — ICI, `/admin/commandes/:id` existe bel
 * et bien (cf. router.tsx), donc chaque ligne de l'historique pointe vers sa
 * fiche commande : léger enrichissement fonctionnel assumé plutôt qu'un lien
 * mort volontairement recréé.
 */
export function AdminClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const customerQuery = useQuery({
    queryKey: ["admin", "customer", id],
    queryFn: () => getAdminCustomerDetail(undefined, id as string),
    enabled: Boolean(id),
    retry: false,
  });

  const toggleMutation = useMutation({
    mutationFn: (action: "block" | "unblock") =>
      action === "block" ? blockAdminCustomer(undefined, id as string) : unblockAdminCustomer(undefined, id as string),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "customer", id] });
      queryClient.invalidateQueries({ queryKey: ["admin", "customers"] });
    },
  });

  if (customerQuery.isLoading) {
    return <p className="text-sm text-muted">Chargement…</p>;
  }

  if (customerQuery.isError) {
    const notFound = customerQuery.error instanceof ApiError && customerQuery.error.status === 404;
    return (
      <div>
        <Link to="/admin/clients" className="text-sm font-medium text-navy hover:underline">
          ← Retour aux clients
        </Link>
        <p className="mt-4 text-sm text-red-700">
          {notFound ? "Client introuvable." : describeAdminError(customerQuery.error)}
        </p>
      </div>
    );
  }

  const customer = customerQuery.data;
  if (!customer) return null;

  const name = [customer.firstName, customer.lastName].filter(Boolean).join(" ") || "Client sans nom";
  const canToggleStatus = customer.status !== "GUEST";
  const toggleAction = customer.status === "BLOCKED" ? "unblock" : "block";
  const toggleLabel = customer.status === "BLOCKED" ? "Débloquer ce client" : "Bloquer ce client";
  const toggleClasses =
    customer.status === "BLOCKED" ? "bg-navy text-cream hover:bg-navy/90" : "bg-red-600 text-white hover:bg-red-700";

  return (
    <div>
      <Link to="/admin/clients" className="text-sm font-medium text-navy hover:underline">
        ← Retour aux clients
      </Link>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink">{name}</h1>
          <span
            className={`mt-2 inline-block rounded-full px-2 py-1 text-xs font-medium ${STATUS_BADGE_CLASSES[customer.status]}`}
          >
            {STATUS_LABELS[customer.status]}
          </span>
        </div>

        {canToggleStatus && (
          <div>
            <button
              type="button"
              disabled={toggleMutation.isPending}
              onClick={() => toggleMutation.mutate(toggleAction)}
              className={`rounded-sm px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${toggleClasses}`}
            >
              {toggleMutation.isPending ? "…" : toggleLabel}
            </button>
            {toggleMutation.isError && (
              <p className="mt-2 text-xs text-red-700">{describeAdminError(toggleMutation.error)}</p>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <InfoCard label="Téléphone" value={customer.phoneE164} />
        <InfoCard label="Email" value={customer.email} />
        <InfoCard label="Commandes" value={customer.ordersCount.toString()} />
        <InfoCard
          label="Valeur vie client"
          value={customer.lifetimeValue.formatted ?? `${customer.lifetimeValue.amountMinor}`}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <InfoCard label="Client depuis" value={formatDateFR(customer.createdAt)} />
        <InfoCard label="Dernière commande" value={formatDateFR(customer.lastOrderAt)} />
        <InfoCard label="Téléphone vérifié" value={customer.phoneVerifiedAt ? "Oui" : "Non"} />
        <InfoCard label="Email vérifié" value={customer.emailVerifiedAt ? "Oui" : "Non"} />
      </div>

      <h2 className="mt-8 font-display text-lg font-semibold text-ink">Historique de commandes</h2>

      <div className="mt-3 overflow-x-auto rounded-sm border border-line bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-line bg-cream text-xs uppercase tracking-wide text-muted">
            <tr>
              <th className="px-4 py-3">N° commande</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3">Paiement</th>
              <th className="px-4 py-3">Articles</th>
              <th className="px-4 py-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {customer.orders.map((order) => (
              <tr key={order.id} className="border-b border-line last:border-0">
                <td className="px-4 py-3 font-medium text-ink">
                  <Link to={`/admin/commandes/${order.id}`} className="text-navy hover:underline">
                    {order.orderNumber}
                  </Link>
                </td>
                <td className="px-4 py-3 text-ink">{formatDateFR(order.placedAt)}</td>
                <td className="px-4 py-3 text-ink">{order.status}</td>
                <td className="px-4 py-3 text-ink">{order.paymentStatus}</td>
                <td className="px-4 py-3 text-ink">{order.itemsCount}</td>
                <td className="px-4 py-3 text-ink">{order.total.formatted}</td>
              </tr>
            ))}
            {customer.orders.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted">
                  Aucune commande enregistrée pour ce client.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-sm border border-line bg-white p-4">
      <p className="text-xs font-medium text-muted">{label}</p>
      <p className="mt-1 text-sm font-semibold text-ink">{value}</p>
    </div>
  );
}
